/**
 * Munger-style latticework: cross-links between Investor Brain principles (BR*)
 * and dictionary terms. Extend this file when adding BR principles or brain terms.
 *
 * Pattern:
 *   term_id → { related_principles, related_terms }
 * Principles keep their own related_terms in principlesLibrary.js;
 * this file enriches dictionary entries and validates the mesh.
 *
 * Expand playbook (brain grows like a real brain):
 *   1. Add BR* in principlesLibrary.js → simpleBrainSeeds
 *   2. Add term in investorDictionaryAdditions.js (if missing)
 *   3. Wire termLattice[term_id] here (additive merge)
 *   4. Optional processScoreBrainLinks.js nudge
 *   5. investorBrainMesh.js → lessonBrainLinks + failurePatternBrainLinks (amalgamation)
 *
 * TODO(BEAM-rename #72): Runtime portfolio code still uses "Grounder" while
 * dictionary/education uses "Anchor". Unify when PR #72 merges.
 */

/** @type {Record<string, { related_principles?: string[], related_terms?: string[] }>} */
export const termLattice = {
  compound_interest: {
    related_principles: ["br1_compound_interest_plain", "br29_snowball_needs_fuel", "fl1_compounding_engine"],
    related_terms: ["exponential_growth", "reinvestment_rate", "snowball_effect", "time_value_of_money"],
  },
  snowball_effect: {
    related_principles: ["br1_compound_interest_plain", "br2_exponential_feels_slow", "br29_snowball_needs_fuel", "fl1_compounding_engine"],
    related_terms: ["compound_interest", "reinvestment_rate", "exponential_growth", "roic"],
  },
  reinvestment_rate: {
    related_principles: ["br29_snowball_needs_fuel", "fl23_reinvestment_rate_matters", "fl1_compounding_engine"],
    related_terms: ["snowball_effect", "compound_interest", "roic", "owner_earnings"],
  },
  reinvestment: {
    related_principles: ["br29_snowball_needs_fuel", "fl23_reinvestment_rate_matters", "fl1_compounding_engine", "p3_compounding_quality"],
    related_terms: ["compound_interest", "compounding", "capital_allocation", "return_on_invested_capital"],
  },
  compounding: {
    related_principles: ["br1_compound_interest_plain", "br2_exponential_feels_slow", "fl1_compounding_engine", "fl23_reinvestment_rate_matters"],
    related_terms: ["compound_interest", "reinvestment", "snowball_effect", "time_horizon"],
  },
  exponential_growth: {
    related_principles: ["br1_compound_interest_plain", "br2_exponential_feels_slow"],
    related_terms: ["compound_interest", "regression_to_the_mean"],
  },
  sunk_cost_fallacy: {
    related_principles: ["br3_sunk_cost_stop", "fl24_sunk_cost_irrelevant"],
    related_terms: ["opportunity_cost", "thesis_break", "emotional_attachment"],
  },
  emotional_attachment: {
    related_principles: ["br4_attachment_bench", "fl26_detach_from_attachment"],
    related_terms: ["endowment_effect", "disposition_effect", "loss_aversion"],
  },
  herd_mentality: {
    related_principles: ["br5_popularity_not_proof"],
    related_terms: ["narrative_risk", "availability_heuristic", "authority_bias"],
  },
  disposition_effect: {
    related_principles: ["br6_trim_winners_review_losers"],
    related_terms: ["loss_aversion", "emotional_attachment", "conviction"],
  },
  hindsight_bias: {
    related_principles: ["br7_hindsight_lie", "fl22_process_over_outcome"],
    related_terms: ["overconfidence_bias", "narrative_fallacy", "base_rate"],
  },
  confirmation_bias: {
    related_principles: ["br8_seek_disproof", "fl18_invert_thesis"],
    related_terms: ["falsification", "evidence_quality", "inversion"],
  },
  regression_to_the_mean: {
    related_principles: ["br9_hot_year_cools"],
    related_terms: ["base_rate", "exponential_growth", "law_of_large_numbers"],
  },
  narrative_fallacy: {
    related_principles: ["br10_story_needs_receipt", "flr3_story_without_evidence"],
    related_terms: ["narrative_risk", "evidence_quality", "optimism_bias"],
  },
  mental_accounting: {
    related_principles: ["br11_same_money_rule", "fl3_true_deployable_cash"],
    related_terms: ["deployable_cash", "opportunity_cost", "risk_capacity"],
  },
  probability: {
    related_principles: ["br12_odds_not_fate", "fl25_think_in_probabilities"],
    related_terms: ["expected_value", "tail_risk", "bayesian_updating"],
  },
  expected_value: {
    related_principles: ["br12_odds_not_fate"],
    related_terms: ["probability", "tail_risk", "asymmetric_bet"],
  },
  tail_risk: {
    related_principles: ["br12_odds_not_fate", "flr1_avoid_ruin_first"],
    related_terms: ["probability", "variance", "leverage"],
  },
  permanent_loss: {
    related_principles: ["flr1_avoid_ruin_first", "flr2_leverage_makes_mistakes_fatal", "fl8_margin_of_safety", "br35_volatility_not_ruin"],
    related_terms: ["downside_risk", "margin_of_safety", "leverage", "risk"],
  },
  opportunity_cost: {
    related_principles: ["br13_best_next_dollar", "p31_opportunity_cost"],
    related_terms: ["capital_allocation", "deployable_cash", "sunk_cost_fallacy"],
  },
  bayesian_updating: {
    related_principles: ["br14_update_when_facts_change"],
    related_terms: ["probability", "evidence_quality", "confirmation_bias"],
  },
  feedback_loop: {
    related_principles: ["br15_loops_feed_themselves"],
    related_terms: ["herd_mentality", "second_order_effects", "leverage"],
  },
  base_rate: {
    related_principles: ["br16_check_base_rates", "p48_confirmation_signal_hierarchy"],
    related_terms: ["survivorship_bias", "regression_to_the_mean", "representativeness_heuristic"],
  },
  survivorship_bias: {
    related_principles: ["br17_count_the_graves"],
    related_terms: ["base_rate", "evidence_quality", "case_study"],
  },
  availability_heuristic: {
    related_principles: ["br18_headlines_feel_louder"],
    related_terms: ["recency_bias", "narrative_risk", "base_rate"],
  },
  second_order_effects: {
    related_principles: ["br19_and_then_what", "p39_second_level_thinking"],
    related_terms: ["feedback_loop", "capital_allocation", "expected_value"],
  },
  inversion: {
    related_principles: ["br20_work_backward_from_failure", "fl18_invert_thesis", "p40_latticework_mental_models"],
    related_terms: ["confirmation_bias", "falsification", "thesis_break"],
  },
  evidence_quality: {
    related_principles: ["br28_verify_not_trust", "br10_story_needs_receipt", "br8_seek_disproof"],
    related_terms: ["confirmation_bias", "narrative_risk", "base_rate"],
  },
  time_value_of_money: {
    related_principles: ["br1_compound_interest_plain", "fl6_price_is_not_value"],
    related_terms: ["compound_interest", "opportunity_cost", "dcf"],
  },
  law_of_large_numbers: {
    related_principles: ["br22_one_trade_not_verdict", "fl22_process_over_outcome"],
    related_terms: ["probability", "regression_to_the_mean", "base_rate"],
  },
  recency_bias: {
    related_principles: ["br21_last_month_not_forever", "br9_hot_year_cools"],
    related_terms: ["availability_heuristic", "base_rate", "regression_to_the_mean"],
  },
  overconfidence_bias: {
    related_principles: ["br23_sixty_percent_not_certain", "br12_odds_not_fate", "br7_hindsight_lie"],
    related_terms: ["hindsight_bias", "probability", "model_risk"],
  },
  loss_aversion: {
    related_principles: ["br24_pain_of_selling", "br6_trim_winners_review_losers", "br3_sunk_cost_stop"],
    related_terms: ["disposition_effect", "sunk_cost_fallacy"],
  },
  power_law: {
    related_principles: ["br25_few_winners_matter"],
    related_terms: ["asymmetric_bet", "conviction", "law_of_large_numbers"],
  },
  asymmetric_bet: {
    related_principles: ["br26_asymmetry_needs_floor", "fl11_concentration_requires_competence"],
    related_terms: ["tail_risk", "expected_value", "position_size"],
  },
  anchoring: {
    related_principles: ["br27_anchor_is_not_value", "fl6_price_is_not_value"],
    related_terms: ["anchoring_bias", "fair_value", "margin_of_safety"],
  },
  anchoring_bias: {
    related_principles: ["br27_anchor_is_not_value", "br32_price_dances_value_walks", "fl6_price_is_not_value"],
    related_terms: ["anchoring", "fair_value", "recency_bias"],
  },
  scenario_analysis: {
    related_principles: ["br12_odds_not_fate", "br19_and_then_what", "fl21_no_one_knows_future", "p37_future_assumption_valuation"],
    related_terms: ["dcf", "discount_rate", "margin_of_safety", "optimism_bias"],
  },
  representativeness_heuristic: {
    related_principles: ["br37_similar_is_not_same", "br5_popularity_not_proof", "br16_check_base_rates"],
    related_terms: ["narrative_risk", "base_rate", "unit_economics"],
  },
  optimism_bias: {
    related_principles: ["br38_optimism_pads_models", "br12_odds_not_fate", "br19_and_then_what"],
    related_terms: ["narrative_fallacy", "regression_to_the_mean", "probability"],
  },
  framing_effect: {
    related_principles: ["br39_framing_changes_mood", "br18_headlines_feel_louder"],
    related_terms: ["availability_heuristic", "recency_bias", "narrative_risk"],
  },
  status_quo_bias: {
    related_principles: ["br40_familiar_isnt_safe", "br4_attachment_bench"],
    related_terms: ["endowment_effect", "emotional_attachment", "inertia_bias"],
  },
  endowment_effect: {
    related_principles: ["br40_familiar_isnt_safe", "br4_attachment_bench", "fl26_detach_from_attachment"],
    related_terms: ["emotional_attachment", "status_quo_bias", "disposition_effect"],
  },
  variance: {
    related_principles: ["br35_volatility_not_ruin", "fl15_volatility_not_always_risk"],
    related_terms: ["standard_deviation", "tail_risk", "drawdown"],
  },
  standard_deviation: {
    related_principles: ["br35_volatility_not_ruin", "br12_odds_not_fate"],
    related_terms: ["variance", "probability", "tail_risk"],
  },
  accounting_quality: {
    related_principles: ["br10_story_needs_receipt", "br28_verify_not_trust", "flr4_accounting_quality_matters", "fl8_margin_of_safety"],
    related_terms: ["accruals", "cash_conversion", "revenue_recognition", "non_gaap"],
  },
  roic: {
    related_principles: ["br29_snowball_needs_fuel", "fl23_reinvestment_rate_matters"],
    related_terms: ["reinvestment_rate", "owner_earnings", "capital_allocation"],
  },
  capital_allocation: {
    related_principles: ["br13_best_next_dollar", "br33_risk_before_return"],
    related_terms: ["opportunity_cost", "reinvestment_rate", "owner_earnings"],
  },
  margin_of_safety: {
    related_principles: ["br30_margin_for_error", "fl8_margin_of_safety", "br27_anchor_is_not_value"],
    related_terms: ["fair_value", "downside_risk", "pe_ratio"],
  },
  diversification: {
    related_principles: ["br34_diversification_seatbelt", "fl10_diversification_protects"],
    related_terms: ["concentration", "correlation", "tail_risk"],
  },
  circle_of_competence: {
    related_principles: ["br36_stay_in_your_circle", "fl12_circle_of_competence", "br16_check_base_rates", "br10_story_needs_receipt"],
    related_terms: ["evidence_quality", "thesis_break", "conviction", "scuttlebutt"],
  },
  scuttlebutt: {
    related_principles: ["br28_verify_not_trust", "br10_story_needs_receipt", "fl17_field_notes_evidence", "br16_check_base_rates"],
    related_terms: ["field_note", "evidence_quality", "verification", "circle_of_competence"],
  },
  beam: {
    related_principles: ["p6_portfolio_role", "fl10_diversification_protects", "br34_diversification_seatbelt"],
    related_terms: ["portfolio_role", "builder", "engine", "anchor", "moonshot", "position_size"],
  },
  portfolio_role: {
    related_principles: ["p6_portfolio_role", "p33_sleeve_architecture", "fl11_concentration_requires_competence"],
    related_terms: ["beam", "position_size", "concentration", "diversification"],
  },
  position_sizing: {
    related_principles: ["br23_sixty_percent_not_certain", "fl10_diversification_protects", "p6_portfolio_role", "p29_concentration_risk"],
    related_terms: ["position_size", "portfolio_role", "concentration", "downside_risk"],
  },
  builder: {
    related_principles: ["p6_portfolio_role", "br31_own_the_business", "p3_compounding_quality", "fl5_stock_business_ownership"],
    related_terms: ["beam", "portfolio_role", "business_model"],
  },
  engine: {
    related_principles: ["p6_portfolio_role", "br29_snowball_needs_fuel", "p11_revenue_quality"],
    related_terms: ["beam", "portfolio_role", "reinvestment_rate", "roic"],
  },
  anchor: {
    related_principles: ["p6_portfolio_role", "br34_diversification_seatbelt", "fl10_diversification_protects"],
    related_terms: ["beam", "portfolio_role", "diversification", "cash_buffer"],
  },
  moonshot: {
    related_principles: ["p6_portfolio_role", "br26_asymmetry_needs_floor", "fl11_concentration_requires_competence"],
    related_terms: ["beam", "portfolio_role", "asymmetric_bet", "tail_risk"],
  },
  position_size: {
    related_principles: ["fl11_concentration_requires_competence", "br25_few_winners_matter", "p10_position_sizing"],
    related_terms: ["concentration", "portfolio_role", "risk_capacity"],
  },
  concentration: {
    related_principles: ["fl11_concentration_requires_competence", "br34_diversification_seatbelt", "br36_stay_in_your_circle"],
    related_terms: ["diversification", "position_size", "correlation"],
  },
};

export const investorBrainLatticePath = {
  id: "investor_brain_lattice",
  title: { en: "Investor Brain Lattice", th: "ตาข่ายสมองนักลงทุน" },
  subtitle: {
    en: "Munger-style mesh: simple rules linked to dictionary terms — expand the lattice, not lesson count.",
    th: "ตาข่ายแบบ Munger: หลักง่ายเชื่อมคำศัพท์ — ขยาย lattice ไม่ใช่จำนวนบทเรียน",
  },
  /** Alternate term ↔ principle pairs for dictionary beginner path */
  term_ids: [
    "snowball_effect",
    "compound_interest",
    "reinvestment_rate",
    "sunk_cost_fallacy",
    "opportunity_cost",
    "bayesian_updating",
    "feedback_loop",
    "base_rate",
    "survivorship_bias",
    "inversion",
    "second_order_effects",
    "tail_risk",
    "representativeness_heuristic",
    "optimism_bias",
    "framing_effect",
    "endowment_effect",
  ],
  principle_ids: [
    "br1_compound_interest_plain",
    "br29_snowball_needs_fuel",
    "br3_sunk_cost_stop",
    "br13_best_next_dollar",
    "br14_update_when_facts_change",
    "br15_loops_feed_themselves",
    "br16_check_base_rates",
    "br17_count_the_graves",
    "br20_work_backward_from_failure",
    "br19_and_then_what",
    "br12_odds_not_fate",
    "br30_margin_for_error",
    "br31_own_the_business",
    "br37_similar_is_not_same",
  ],
};

/**
 * Merge lattice links onto a dictionary term (additive).
 */
export function enrichDictionaryTermWithLattice(term) {
  if (!term?.id) return term;
  const patch = termLattice[term.id];
  if (!patch) return term;
  const mergeUnique = (a = [], b = []) => [...new Set([...a, ...b])];
  return {
    ...term,
    related_terms: mergeUnique(term.related_terms, patch.related_terms),
    related_principles: mergeUnique(term.related_principles, patch.related_principles),
  };
}

export function getLatticePrinciplesForTerm(termId) {
  return termLattice[termId]?.related_principles || [];
}

export function validateBrainLattice(principleIds) {
  const principleSet = new Set(principleIds);
  const invalidPrincipleLinks = [];
  const orphanTerms = [];

  Object.entries(termLattice).forEach(([termId, patch]) => {
    (patch.related_principles || []).forEach(pid => {
      if (!principleSet.has(pid)) invalidPrincipleLinks.push(`${termId}->${pid}`);
    });
  });

  return {
    term_count: Object.keys(termLattice).length,
    invalid_principle_links: invalidPrincipleLinks,
    orphan_terms: orphanTerms,
    valid: invalidPrincipleLinks.length === 0,
  };
}
